import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { amount, money, percent, todayYmd } from '../lib/format';
import { toast } from '../lib/toast';
import { useContacts } from '../lib/contacts-api';
import { UNIT_LABELS, useItems, type Item } from '../lib/items-api';
import {
  CREATABLE_DOCUMENT_TYPES,
  DATE_ONLY_PATTERN,
  DOCUMENT_TYPE_LABELS,
  MONEY_PATTERN,
  PERCENT_PATTERN,
  POSITIVE_QUANTITY_PATTERN,
  invoiceErrorMessage,
  invoiceFieldProblems,
  likelyRequiresPeppolReference,
  toastInvoiceError,
  trimRateText,
  useCreateInvoice,
  useInvoice,
  useUpdateInvoice,
  type CreatableDocumentType,
  type CreateInvoiceBody,
  type InvoiceLineBody,
  type SalesInvoice,
  type UpdateInvoiceBody,
} from '../lib/invoices-api';
import {
  Button,
  Card,
  CardHeader,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';

/**
 * Taslak düzenleyici — `/invoices/new` ve `/invoices/:id/edit` AYNI bileşendir.
 *
 * ⚠ YALNIZ TASLAK. Düzenlenmiş belge DEĞİŞMEZ (DB tetikleyicisi
 * `sales_invoice_immutable`); bu ekran ISSUED bir belgeye açılırsa formu hiç
 * göstermez, doğrudan "düzeltme yolu kredi notudur" der.
 *
 * ⚠⚠ TOPLAMLAR İSTEMCİDE HESAPLANMAZ.
 *
 * KDV, (oran, kategori) grubunun TOPLAM matrahı üzerinden BİR KEZ hesaplanır
 * (EN 16931 BR-CO-17). Satır satır hesaplayıp toplamak KURUŞ SAPMASI üretir ve
 * bir muhasebe panelinde iki farklı rakam görmek, hangisinin doğru olduğunu
 * kimsenin bilememesi demektir. Bu yüzden burada tek bir çarpma bile yoktur:
 * taslak KAYDEDİLİR, sunucunun döndürdüğü `subtotalExclVat` / `vatAmount` /
 * `totalInclVat` / `vatBreakdown` GÖSTERİLİR.
 *
 * ⚠ PARA VE ORAN ALANLARI `string` STATE'TİR. `Number()`/`parseFloat` yok:
 * `(1.005).toFixed(2) === '1.00'` sınıfı kuruş kaybı değer double'a çevrildiği
 * anda olur. Girdi kutusundaki ham metin doğrudan gövdeye konur.
 */

interface LineDraft {
  /** React anahtarı — satır silinip eklendiğinde girdi odağı atlamasın diye. */
  key: string;
  itemId: string;
  /** Boşsa kalemin adı BELGENİN YASAL DİLİNDE (Fransızca) kullanılır. */
  description: string;
  quantity: string;
  /** Boşsa katalog fiyatı dondurulur. */
  unitPrice: string;
  discountPct: string;
  /** Boşsa katalog oranı. Ters yükümlülükte çözümleyici zaten sıfırlar. */
  vatRate: string;
  servicePeriodStart: string;
  servicePeriodEnd: string;
}

let lineCounter = 0;

function emptyLine(): LineDraft {
  lineCounter += 1;
  return {
    key: `l${lineCounter}`,
    itemId: '',
    description: '',
    quantity: '1',
    unitPrice: '',
    discountPct: '',
    vatRate: '',
    servicePeriodStart: '',
    servicePeriodEnd: '',
  };
}

interface HeaderDraft {
  contactId: string;
  documentType: CreatableDocumentType;
  issueDate: string;
  supplyDate: string;
  deliveryDate: string;
  dueDate: string;
  documentDiscount: string;
  buyerReference: string;
  orderReference: string;
  deliveryAddress: string;
  notes: string;
  internalNotes: string;
}

function emptyHeader(): HeaderDraft {
  return {
    contactId: '',
    documentType: 'INVOICE',
    // Brüksel günü — `new Date().toISOString()` UTC gününü verir ve yaz
    // saatinde gece yarısı sonrası bir ÖNCEKİ güne yazar.
    issueDate: todayYmd(),
    supplyDate: '',
    deliveryDate: '',
    dueDate: '',
    documentDiscount: '',
    buyerReference: '',
    orderReference: '',
    deliveryAddress: '',
    notes: '',
    internalNotes: '',
  };
}

function headerFromInvoice(invoice: SalesInvoice): HeaderDraft {
  return {
    contactId: invoice.contactId,
    // Kayıtlı belgede tür DEĞİŞMEZ; select zaten kilitli gösterilir.
    documentType: invoice.documentType === 'PROFORMA' ? 'PROFORMA' : 'INVOICE',
    issueDate: invoice.issueDate,
    supplyDate: invoice.supplyDate,
    deliveryDate: invoice.deliveryDate ?? '',
    dueDate: invoice.dueDate,
    documentDiscount: invoice.discountAmount,
    buyerReference: invoice.buyerReference ?? '',
    orderReference: invoice.orderReference ?? '',
    deliveryAddress: invoice.deliveryAddress ?? '',
    notes: invoice.notes ?? '',
    internalNotes: invoice.internalNotes ?? '',
  };
}

function linesFromInvoice(invoice: SalesInvoice): LineDraft[] {
  const rows = invoice.lines ?? [];
  if (rows.length === 0) return [emptyLine()];
  return rows.map((line) => {
    lineCounter += 1;
    return {
      key: `l${lineCounter}`,
      itemId: line.itemId ?? '',
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      /*
       * ⚠ İNDİRİM VE ORAN SONDAKİ SIFIRLARDAN ARINDIRILIR ama METİN üzerinde:
       * '21.00' → '21'. Sunucu iki ondalıkla döndürüyor; kullanıcıya '21.00'
       * göstermek zararsız olurdu ama bir sonraki kaydetmede aynen geri
       * gideceği için değerin hiçbir aşamada double'a uğramaması şart.
       */
      discountPct: line.discountPct === '0.00' ? '' : trimRateText(line.discountPct),
      vatRate: trimRateText(line.vatRate),
      servicePeriodStart: line.servicePeriodStart ?? '',
      servicePeriodEnd: line.servicePeriodEnd ?? '',
    };
  });
}

// ────────────────────────────────────────────────────── sınır doğrulaması

/**
 * Gövdeyi göndermeden önceki son bakış.
 *
 * ⚠ Bu bir KAPI DEĞİL, KISAYOL: asıl doğrulama sunucudadır (DTO desenleri + DB
 * kısıtları) ve buradaki her kural onun ikizidir. Var olma sebebi, operatörün
 * hatayı ağ gidiş-dönüşünü beklemeden ve HANGİ SATIRDA olduğunu görerek
 * öğrenmesi: sunucunun `fields` dizisi satır satırlı değil, yalnız `lines` der.
 */
function validate(header: HeaderDraft, lines: LineDraft[]): Map<string, string> {
  const problems = new Map<string, string>();

  if (!header.contactId) problems.set('contactId', 'Bir müşteri seçin.');

  const dateFields: Array<[keyof HeaderDraft, boolean]> = [
    ['issueDate', true],
    ['supplyDate', false],
    ['deliveryDate', false],
    ['dueDate', false],
  ];
  for (const [field, required] of dateFields) {
    const value = header[field];
    if (typeof value !== 'string') continue;
    if (value === '') {
      if (required) problems.set(field, 'Bu tarih zorunlu.');
      continue;
    }
    if (!DATE_ONLY_PATTERN.test(value)) {
      problems.set(field, 'YYYY-AA-GG biçiminde bir takvim günü olmalı.');
    }
  }

  if (
    header.issueDate &&
    header.dueDate &&
    DATE_ONLY_PATTERN.test(header.issueDate) &&
    DATE_ONLY_PATTERN.test(header.dueDate) &&
    header.dueDate < header.issueDate
  ) {
    // ISO tarihleri metin olarak sıralanır; karşılaştırma için `Date` gerekmez.
    problems.set('dueDate', 'Vade, düzenleme tarihinden önce olamaz.');
  }

  if (header.documentDiscount !== '' && !MONEY_PATTERN.test(header.documentDiscount)) {
    problems.set('documentDiscount', 'Nokta ile ve en fazla iki ondalık (örn. 50.00).');
  }

  lines.forEach((line) => {
    const at = (field: string) => `line:${line.key}:${field}`;

    if (!line.itemId) {
      problems.set(
        at('itemId'),
        'Katalog kalemi zorunlu: serbest metin satırında KDV nitelendirmesi bilinmez.',
      );
    }
    if (!POSITIVE_QUANTITY_PATTERN.test(line.quantity)) {
      problems.set(at('quantity'), 'Sıfırdan büyük, nokta ile, en fazla dört ondalık.');
    }
    if (line.unitPrice !== '' && !MONEY_PATTERN.test(line.unitPrice)) {
      problems.set(at('unitPrice'), 'Nokta ile ve en fazla iki ondalık.');
    }
    if (line.discountPct !== '' && !PERCENT_PATTERN.test(line.discountPct)) {
      problems.set(at('discountPct'), '0 ile 100 arasında bir yüzde.');
    }
    if (line.vatRate !== '' && !PERCENT_PATTERN.test(line.vatRate)) {
      problems.set(at('vatRate'), '0 ile 100 arasında bir oran.');
    }

    // ⚠ Dönem başı ve sonu BİRLİKTE verilir ya da hiç verilmez
    // (`CHK_sales_invoice_lines_period`); yalnız başlangıç göndermek 400'dür.
    const hasStart = line.servicePeriodStart !== '';
    const hasEnd = line.servicePeriodEnd !== '';
    if (hasStart !== hasEnd) {
      problems.set(
        at(hasStart ? 'servicePeriodEnd' : 'servicePeriodStart'),
        'Hizmet dönemi başı ve sonu BİRLİKTE verilir ya da hiç verilmez.',
      );
    }
  });

  return problems;
}

/** ⚠ Boş metin gönderilmez: `''` çoğu alanda 400'dür, `undefined` "dokunma"dır. */
function optional(value: string): string | undefined {
  return value === '' ? undefined : value;
}

/** `null` = ALANI TEMİZLE. Nullable alanlarda boş kutu bunu ifade eder. */
function nullable(value: string): string | null {
  return value === '' ? null : value;
}

function toLineBody(line: LineDraft): InvoiceLineBody {
  const period =
    line.servicePeriodStart === ''
      ? { servicePeriodStart: null, servicePeriodEnd: null }
      : { servicePeriodStart: line.servicePeriodStart, servicePeriodEnd: line.servicePeriodEnd };

  return {
    itemId: line.itemId,
    quantity: line.quantity,
    ...(optional(line.description) === undefined ? {} : { description: line.description }),
    ...(optional(line.unitPrice) === undefined ? {} : { unitPrice: line.unitPrice }),
    ...(optional(line.discountPct) === undefined ? {} : { discountPct: line.discountPct }),
    ...(optional(line.vatRate) === undefined ? {} : { vatRate: line.vatRate }),
    ...period,
  };
}

// ───────────────────────────────────────────────────────────────── sayfa

export function InvoiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const readOnly = isReadOnly(user);

  const existing = useInvoice(id);
  const invoice = existing.data ?? null;

  const [header, setHeader] = useState<HeaderDraft>(emptyHeader);
  const [lines, setLines] = useState<LineDraft[]>(() => [emptyLine()]);
  const [problems, setProblems] = useState<Map<string, string>>(new Map());
  /** Kaydedilmemiş değişiklik var mı — toplam paneli bunu söylemek zorunda. */
  const [dirty, setDirty] = useState(false);
  /** Son kaydın SUNUCUDAN dönen hâli; toplamlar buradan okunur. */
  const [saved, setSaved] = useState<SalesInvoice | null>(null);

  const customers = useContacts({ limit: 200, isCustomer: true, active: true });
  const catalog = useItems({ active: true, limit: 200 });
  const itemsById = useMemo(() => {
    const map = new Map<string, Item>();
    for (const item of catalog.data?.items ?? []) map.set(item.id, item);
    return map;
  }, [catalog.data]);

  const create = useCreateInvoice();
  const update = useUpdateInvoice();
  const busy = create.isPending || update.isPending;

  /*
   * Sunucudan gelen taslak forma BİR KEZ yüklenir (belge kimliği değişince
   * yeniden). Her render'da yüklemek, kullanıcının yazdığını her tazelemede
   * geri alırdı.
   */
  useEffect(() => {
    if (!invoice) return;
    setHeader(headerFromInvoice(invoice));
    setLines(linesFromInvoice(invoice));
    setSaved(invoice);
    setDirty(false);
    /*
     * ⚠ Bağımlılık `invoice` NESNESİ DEĞİL, kimliği ve `updatedAt` damgası.
     * Nesneye bağlansaydı her yeniden çekme formu sıfırlardı; damgaya bağlı
     * olduğu için yalnızca belge GERÇEKTEN değiştiğinde (kaydetme sonrası
     * kanonikleştirilmiş hâl geldiğinde) yeniden yükleniyor.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bilinçli: yukarıdaki gerekçe
  }, [invoice?.id, invoice?.updatedAt]);

  function patchHeader(patch: Partial<HeaderDraft>) {
    setHeader((h) => ({ ...h, ...patch }));
    setDirty(true);
  }

  function patchLine(key: string, patch: Partial<LineDraft>) {
    setLines((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    setDirty(true);
  }

  /**
   * Kalem seçilince fiyat, oran ve açıklama katalogtan DOLDURULUR.
   *
   * ⚠ Değerler gövdeye de gider, yani katalog fiyatı BELGEYE DONAR. Boş
   * bırakıp sunucunun katalogtan almasına izin vermek de aynı sonucu verirdi;
   * doldurmanın farkı, operatörün neyi kestiğini kaydetmeden ÖNCE görmesidir.
   */
  function selectItem(key: string, itemId: string) {
    const item = itemsById.get(itemId);
    patchLine(key, {
      itemId,
      ...(item
        ? { unitPrice: item.unitPrice, vatRate: trimRateText(item.vatRate) }
        : { unitPrice: '', vatRate: '' }),
    });
  }

  function submit() {
    const found = validate(header, lines);
    setProblems(found);
    if (found.size > 0) {
      toast.errorText('Formda geçersiz alanlar var; işaretli kutuları düzeltin.');
      return;
    }

    const common = {
      issueDate: header.issueDate,
      ...(optional(header.supplyDate) === undefined ? {} : { supplyDate: header.supplyDate }),
      deliveryDate: nullable(header.deliveryDate),
      ...(optional(header.dueDate) === undefined ? {} : { dueDate: header.dueDate }),
      /*
       * ⚠ BOŞ KUTU "DOKUNMA" DEĞİL, "SIFIRLA" DEMEKTİR.
       *
       * `documentDiscount` DTO'da `@Provided()`tır: anahtar hiç konmazsa sunucu
       * mevcut indirimi KORUR. Kutuyu boşaltan operatör ise indirimi kaldırmak
       * ister — anahtar atlanırsa 50 €'luk indirim belgede sessizce kalır ve
       * toplam beklediğinden düşük çıkar. `MONEY_PATTERN` '0'ı kabul eder.
       */
      documentDiscount: header.documentDiscount === '' ? '0' : header.documentDiscount,
      buyerReference: nullable(header.buyerReference),
      orderReference: nullable(header.orderReference),
      deliveryAddress: nullable(header.deliveryAddress),
      notes: nullable(header.notes),
      internalNotes: nullable(header.internalNotes),
    };

    const onSaved = (result: SalesInvoice, created: boolean) => {
      setSaved(result);
      setDirty(false);
      setProblems(new Map());
      toast.success(created ? 'Taslak oluşturuldu.' : 'Taslak kaydedildi.');
      if (created) {
        // Yeni taslak artık bir kimliğe sahip: aynı ekran onun DÜZENLEME
        // formuna dönüşür. `replace` ile geri tuşu "yeni taslak"a dönmez.
        void navigate(`/invoices/${result.id}/edit`, { replace: true });
      }
    };

    const onFailed = (err: unknown) => {
      // Satir anahtarlari SIRAYLA gecilir: sunucu satiri indeksle suclar.
      setProblems(
        invoiceFieldProblems(
          err,
          lines.map((l) => l.key),
        ),
      );
      toastInvoiceError(err);
    };

    if (id) {
      const body: UpdateInvoiceBody = {
        ...common,
        contactId: header.contactId,
        lines: lines.map(toLineBody),
      };
      update.mutate(
        { id, body },
        { onSuccess: (result) => onSaved(result, false), onError: onFailed },
      );
      return;
    }

    const body: CreateInvoiceBody = {
      ...common,
      contactId: header.contactId,
      documentType: header.documentType,
      lines: lines.map(toLineBody),
    };
    create.mutate(body, { onSuccess: (result) => onSaved(result, true), onError: onFailed });
  }

  // ── kapılar ──────────────────────────────────────────────────────────

  if (readOnly) {
    return (
      <Card>
        <ErrorState message="Salt okuma yetkisiyle belge oluşturulamaz veya değiştirilemez." />
      </Card>
    );
  }

  if (id && existing.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size={24} />
      </div>
    );
  }

  if (id && existing.isError) {
    return (
      <Card>
        <ErrorState message={invoiceErrorMessage(existing.error)} />
      </Card>
    );
  }

  /*
   * ⚠ DÜZENLENMİŞ BELGE BU EKRANDA AÇILMAZ. Formu gösterip kaydetmede hata
   * vermek, operatöre yaptığı işi kaybettirirdi; üstelik asıl mesaj
   * ("düzeltme yolu kredi notudur") bir toast'ta kaybolurdu.
   */
  if (invoice && invoice.status !== 'DRAFT') {
    return (
      <Card>
        <CardHeader title="Bu belge artık düzenlenemez" />
        <div className="space-y-3 px-4 py-4 text-sm text-[var(--c-ink-soft)]">
          <p>
            Belge <strong>{invoice.documentNumber ?? 'numarasız'}</strong> durumu{' '}
            <strong>{invoice.status}</strong>. Numara almış bir belge kesintisiz dizinin parçasıdır;
            değiştirilmesi dizide denetimde savunulamayacak bir delik açar.
          </p>
          <p>
            Düzeltmenin tek hukuki yolu <strong>kredi notudur</strong> (KB nr. 1 md. 12) ve belge
            detayından kesilir.
          </p>
          <Link
            to={`/invoices/${invoice.id}`}
            className="inline-block text-[var(--c-brand)] hover:underline"
          >
            Belgeye git →
          </Link>
        </div>
      </Card>
    );
  }

  const problem = (key: string) => problems.get(key) ?? null;

  const customerRows = customers.data?.items ?? [];
  /*
   * ⚠ Taslağın carisi listede OLMAYABİLİR: pasifleşmiş ya da ilk 200 kaydın
   * dışında kalmış olabilir. Seçenek eklenmezse `<select>` boş görünür ve
   * operatör "müşteri silinmiş" sanıp yeniden seçer — oysa değer state'te
   * duruyordur ve seçim yapmak onu DEĞİŞTİRİR.
   */
  const missingCustomer =
    header.contactId !== '' && !customerRows.some((row) => row.id === header.contactId);

  /*
   * ⚠⚠ TASLAKTAKİ ERKEN UYARI — kapı DEĞİL. Asıl kapı sunucuda, düzenleme
   * anında; burası operatörün "Bu adım GERİ ALINAMAZ" onayına gelmeden ÖNCE
   * aynı eksiği görmesi için var. Taslağı kaydetmeyi bloke etmiyor.
   */
  const selectedContact = customerRows.find((row) => row.id === header.contactId) ?? null;
  const peppolReferenceMissing =
    likelyRequiresPeppolReference(header.documentType, selectedContact) &&
    header.buyerReference.trim() === '' &&
    header.orderReference.trim() === '';

  return (
    <>
      <PageHeader
        title={id ? 'Taslağı düzenle' : 'Yeni taslak'}
        description="Taslak numarasızdır ve hiçbir KDV dönemine düşmez. Numara, KDV kararı ve zorunlu ibare yalnız 'faturayı düzenle' adımında donar."
        actions={
          <>
            {saved && (
              <Button variant="secondary" onClick={() => void navigate(`/invoices/${saved.id}`)}>
                Belgeyi görüntüle
              </Button>
            )}
            <Button onClick={submit} loading={busy}>
              {id ? 'Taslağı kaydet' : 'Taslağı oluştur'}
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {/* ── müşteri ve belge türü ─────────────────────────────────── */}
          <Card>
            <CardHeader
              title="Müşteri ve belge"
              description="Müşteri tipi, ülkesi ve KDV numarası çözümleyicinin girdisidir — yanlış cari yanlış kutu demektir."
            />
            <div className="grid gap-3 px-4 py-4 sm:grid-cols-2">
              <Select
                label="Müşteri"
                value={header.contactId}
                onChange={(e) => patchHeader({ contactId: e.target.value })}
                error={problem('contactId')}
                /*
                 * ⚠ ÜÇÜNCÜ DURUM: sorgu HATA da verebilir. Yalnız `isPending`e
                 * bakılsaydı liste boş, kutu da "Yalnız aktif müşteriler
                 * listelenir" diyor olurdu — operatör hiç müşterisi olmadığını
                 * sanardı. Boş liste ile ulaşılamayan liste ayrı şeylerdir.
                 */
                hint={
                  customers.isError
                    ? `Müşteri listesi yüklenemedi: ${invoiceErrorMessage(customers.error)}`
                    : customers.isPending
                      ? 'Yükleniyor…'
                      : 'Yalnız aktif müşteriler listelenir.'
                }
              >
                <option value="">— seçin —</option>
                {missingCustomer && (
                  <option value={header.contactId}>
                    {invoice ? invoice.billingName : 'Seçili cari'} (listede yok — pasif olabilir)
                  </option>
                )}
                {customerRows.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}
                    {contact.vatNumber ? ` — ${contact.vatNumber}` : ''}
                  </option>
                ))}
              </Select>

              <Select
                label="Belge türü"
                value={header.documentType}
                disabled={Boolean(id)}
                onChange={(e) =>
                  patchHeader({ documentType: e.target.value as CreatableDocumentType })
                }
                hint={
                  id
                    ? 'Kayıtlı bir taslağın türü değişmez: seri de değişirdi. Doğru yol yeni taslaktır.'
                    : 'Kredi notu buradan açılmaz — düzelttiği faturanın detayından kesilir (md. 77 §1).'
                }
              >
                {CREATABLE_DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {DOCUMENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>
          </Card>

          {/* ── satırlar ──────────────────────────────────────────────── */}
          <Card>
            <CardHeader
              title="Satırlar"
              description="Her satır bir katalog kalemine bağlanır: KDV nitelendirmesi (hizmet/mal, elektronik hizmet, taşınmaz işi, donanım tasarrufu) yalnız katalogda yaşar."
            />

            <div className="space-y-4 px-4 py-4">
              {/*
               * Satıra EŞLENEMEYEN satır hatası buraya düşer (sunucu satır
               * listesinin tamamını suçladığında ya da indeks form kayarken
               * artık bir satıra denk gelmediğinde). Gösterilmezse "kaydedilmedi
               * ama hiçbir alan kırmızı değil" ekranı çıkardı.
               */}
              {problem('lines') !== null && (
                <p className="text-sm text-[var(--c-danger)]">{problem('lines')}</p>
              )}

              {lines.map((line, index) => {
                const item = itemsById.get(line.itemId) ?? null;
                const at = (field: string) => `line:${line.key}:${field}`;

                return (
                  <div
                    key={line.key}
                    className="rounded-[var(--r-md)] border border-[var(--c-border)] p-3"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold text-[var(--c-ink-soft)]">
                        Satır {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={lines.length === 1}
                        onClick={() => {
                          setLines((rows) => rows.filter((row) => row.key !== line.key));
                          setDirty(true);
                        }}
                      >
                        Satırı sil
                      </Button>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Select
                          label="Kalem"
                          value={line.itemId}
                          onChange={(e) => selectItem(line.key, e.target.value)}
                          error={problem(at('itemId'))}
                          hint={
                            catalog.isError
                              ? `Katalog yüklenemedi: ${invoiceErrorMessage(catalog.error)}`
                              : item
                                ? `${item.supplyKind === 'GOODS' ? 'Mal' : 'Hizmet'} · birim: ${
                                    UNIT_LABELS[item.unit]
                                  }`
                                : catalog.isPending
                                  ? 'Yükleniyor…'
                                  : 'Tek seferlik iş için katalogda genel bir kalem açın.'
                          }
                        >
                          <option value="">— seçin —</option>
                          {/*
                           * Müşteri seçicisindeki `missingCustomer` ile AYNI
                           * tuzak: taslağın kalemi pasifleşmiş ya da ilk 200
                           * kaydın dışında kalmış olabilir. Yedek seçenek
                           * basılmazsa kutu BOŞ görünür — oysa `line.itemId`
                           * state'te ve gövdede durmaya devam eder; operatör
                           * "kalem silinmiş" sanıp başka bir kalem seçer ve
                           * farkında olmadan satırın KDV nitelendirmesini
                           * değiştirir.
                           */}
                          {line.itemId !== '' && !itemsById.has(line.itemId) && (
                            <option value={line.itemId}>
                              Seçili kalem (listede yok — pasif olabilir)
                            </option>
                          )}
                          {(catalog.data?.items ?? []).map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.name.tr ?? row.name.fr}
                            </option>
                          ))}
                        </Select>
                      </div>

                      <Input
                        label="Adet"
                        value={line.quantity}
                        inputMode="decimal"
                        onChange={(e) => patchLine(line.key, { quantity: e.target.value })}
                        error={problem(at('quantity'))}
                        hint="Nokta ile; saat kesirli olabilir (7.3333)."
                      />

                      <Input
                        label="Birim fiyat (KDV hariç)"
                        value={line.unitPrice}
                        inputMode="decimal"
                        onChange={(e) => patchLine(line.key, { unitPrice: e.target.value })}
                        error={problem(at('unitPrice'))}
                        hint="Boş bırakılırsa katalog fiyatı dondurulur."
                      />

                      <Input
                        label="İndirim %"
                        value={line.discountPct}
                        inputMode="decimal"
                        onChange={(e) => patchLine(line.key, { discountPct: e.target.value })}
                        error={problem(at('discountPct'))}
                        hint="KB nr. 1 md. 5 §1: indirim faturada GÖRÜNÜR olmalı."
                      />

                      <Input
                        label="KDV oranı %"
                        value={line.vatRate}
                        inputMode="decimal"
                        onChange={(e) => patchLine(line.key, { vatRate: e.target.value })}
                        error={problem(at('vatRate'))}
                        hint="Ters yükümlülük ya da muafiyet çıkarsa çözümleyici oranı zaten sıfırlar."
                      />

                      <div className="sm:col-span-2">
                        <Input
                          label="Açıklama (faturaya basılır)"
                          value={line.description}
                          onChange={(e) => patchLine(line.key, { description: e.target.value })}
                          hint="Boş bırakılırsa kalemin FRANSIZCA adı basılır (Charleroi → Valonya → Taaldecreet 19.07.1973)."
                        />
                      </div>

                      <Input
                        label="Hizmet dönemi başı"
                        type="date"
                        value={line.servicePeriodStart}
                        onChange={(e) =>
                          patchLine(line.key, { servicePeriodStart: e.target.value })
                        }
                        error={problem(at('servicePeriodStart'))}
                      />
                      <Input
                        label="Hizmet dönemi sonu"
                        type="date"
                        value={line.servicePeriodEnd}
                        onChange={(e) => patchLine(line.key, { servicePeriodEnd: e.target.value })}
                        error={problem(at('servicePeriodEnd'))}
                        hint="Abonelikte zorunlu sayın: sürekli hizmette ifa dönemin SONUDUR (md. 22 §2)."
                      />
                    </div>
                  </div>
                );
              })}

              {problems.has('lines') && (
                <p className="text-xs text-[var(--c-danger)]">{problems.get('lines')}</p>
              )}

              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setLines((rows) => [...rows, emptyLine()]);
                  setDirty(true);
                }}
              >
                Satır ekle
              </Button>
            </div>
          </Card>

          {/* ── tarihler ──────────────────────────────────────────────── */}
          <Card>
            <CardHeader
              title="Tarihler"
              description="Belgenin hangi KDV dönemine düştüğü TESLİM tarihinden türer, düzenleme tarihinden değil."
            />
            <div className="grid gap-3 px-4 py-4 sm:grid-cols-2">
              <Input
                label="Düzenleme tarihi"
                type="date"
                value={header.issueDate}
                onChange={(e) => patchHeader({ issueDate: e.target.value })}
                error={problem('issueDate')}
                hint="Numaranın yılı bu tarihten gelir."
              />
              <Input
                label="Teslim / ifa tarihi"
                type="date"
                value={header.supplyDate}
                onChange={(e) => patchHeader({ supplyDate: e.target.value })}
                error={problem('supplyDate')}
                hint="Vergiyi doğuran olay (md. 22). Boşsa düzenleme tarihi."
              />
              <Input
                label="Sevk tarihi"
                type="date"
                value={header.deliveryDate}
                onChange={(e) => patchHeader({ deliveryDate: e.target.value })}
                error={problem('deliveryDate')}
                hint="EN 16931 BT-72; md. 39bis'te nakliye kanıtının parçası."
              />
              <Input
                label="Vade"
                type="date"
                value={header.dueDate}
                onChange={(e) => patchHeader({ dueDate: e.target.value })}
                error={problem('dueDate')}
                hint="Boşsa carinin ödeme vadesi. B2B'de sözleşmeyle azami 60 gün (WER Kitap VI)."
              />
            </div>
          </Card>

          {/* ── referanslar ve notlar ─────────────────────────────────── */}
          <Card>
            <CardHeader title="Referanslar ve notlar" />
            <div className="grid gap-3 px-4 py-4 sm:grid-cols-2">
              {/*
                ⚠⚠ ERKEN UYARI — bu SEÇİLİ MÜŞTERİYE ÖZGÜ ve kaydetmeyi
                bloke etmiyor. Statik ipucu (aşağıda, alıcı referansı
                alanında) her taslakta aynı metni gösterir ve gözden
                kaçırılabilir; bu kutu yalnız GERÇEKTEN gerektiğinde belirir.
              */}
              {peppolReferenceMissing && (
                <p className="sm:col-span-2 rounded-[var(--r-md)] border border-[var(--c-warn)] bg-[var(--c-warn-soft)] px-3 py-2 text-xs text-[var(--c-warn)]">
                  Bu müşteri Belçika'da yerleşik bir KDV mükellefi/kamu kurumu ya da periyodik
                  beyan veriyor — bu belge Peppol'den geçmek zorunda (md. 53 §2bis) ve alıcı ya da
                  sipariş referansından biri girilmeden <strong>düzenlenemez</strong>. Belge
                  düzenlendikten sonra eklenemez; taslak kaydedilebilir ama şimdi doldurmanız,
                  "faturayı düzenle" onayına geldiğinizde geri dönmenizi önler.
                </p>
              )}
              <Input
                label="Alıcı referansı (BT-10)"
                value={header.buyerReference}
                onChange={(e) => patchHeader({ buyerReference: e.target.value })}
                error={problem('buyerReference')}
                hint="Peppol'de BT-10 ile BT-13'ten EN AZ BİRİ zorunlu (R003). Yurtiçi B2B faturasında ikisi de boşsa belge DÜZENLENEMEZ — düzenlendikten sonra eklenemeyeceği için kapı düzenleme anındadır."
              />
              <Input
                label="Sipariş referansı (BT-13)"
                value={header.orderReference}
                onChange={(e) => patchHeader({ orderReference: e.target.value })}
                error={problem('orderReference')}
              />

              <Input
                label="Belge indirimi (KDV hariç)"
                value={header.documentDiscount}
                inputMode="decimal"
                onChange={(e) => patchHeader({ documentDiscount: e.target.value })}
                error={problem('documentDiscount')}
                hint="BT-92 — satır indiriminden AYRI. Matrahtan düşülür ve satırlara en büyük artık yöntemiyle dağıtılır."
              />
              <Input
                label="Teslim adresi"
                value={header.deliveryAddress}
                onChange={(e) => patchHeader({ deliveryAddress: e.target.value })}
                error={problem('deliveryAddress')}
              />

              <div className="sm:col-span-2">
                <Textarea
                  label="Not (belgeye BASILIR)"
                  value={header.notes}
                  onChange={(e) => patchHeader({ notes: e.target.value })}
                  error={problem('notes')}
                  hint="Belgeye basıldığı için düzenlendikten sonra DEĞİŞMEZ."
                />
              </div>
              <div className="sm:col-span-2">
                <Textarea
                  label="İç not (basılmaz)"
                  value={header.internalNotes}
                  onChange={(e) => patchHeader({ internalNotes: e.target.value })}
                  error={problem('internalNotes')}
                  hint="Basılmadığı için düzenlendikten sonra da değiştirilebilir."
                />
              </div>
            </div>
          </Card>
        </div>

        {/* ── toplamlar: SUNUCUDAN ──────────────────────────────────── */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <ServerTotals invoice={saved} dirty={dirty} />
        </div>
      </div>
    </>
  );
}

/**
 * Toplam paneli — tek kaynağı SUNUCUNUN DÖNDÜĞÜ BELGEDİR.
 *
 * ⚠ Kaydedilmemiş bir değişiklik varken eski toplam GÖSTERİLİR ama açıkça
 * "bayat" damgalanır. Yerel bir tahmin göstermek daha "canlı" görünürdü ve tam
 * olarak bu yüzden yapılmıyor: operatör iki farklı rakam görürse hangisinin
 * belgeye yazılacağını bilemez ve KDV, satır satır değil GRUP matrahı üzerinden
 * hesaplandığı için tahmin ile gerçek kuruş sapar (EN 16931 BR-CO-17).
 */
function ServerTotals({ invoice, dirty }: { invoice: SalesInvoice | null; dirty: boolean }) {
  return (
    <Card>
      <CardHeader
        title="Toplamlar"
        description="Sunucu hesaplar. Panel tek bir çarpma bile yapmaz."
      />

      {invoice === null ? (
        <p className="px-4 py-4 text-sm text-[var(--c-ink-soft)]">
          Taslak kaydedildiğinde matrah, KDV ve genel toplam burada görünecek.
        </p>
      ) : (
        <div className="px-4 py-4 text-sm">
          {dirty && (
            <p className="mb-3 rounded-[var(--r-md)] bg-[var(--c-warn-soft)] px-3 py-2 text-xs text-[var(--c-warn)]">
              Kaydedilmemiş değişiklik var — aşağıdaki tutarlar SON KAYDA aittir.
            </p>
          )}

          <dl className="space-y-1">
            <Row label="Matrah (BT-109)" value={money(invoice.subtotalExclVat)} />
            {invoice.discountAmount !== '0.00' && (
              <Row label="Belge indirimi" value={money(invoice.discountAmount)} />
            )}
            <Row label="KDV" value={money(invoice.vatAmount)} />
            {invoice.roundingAmount !== '0.00' && (
              <Row label="Yuvarlama (BT-114)" value={money(invoice.roundingAmount)} />
            )}
            <div className="border-t border-[var(--c-border)] pt-1">
              <Row label="Genel toplam" value={money(invoice.totalInclVat)} strong />
            </div>
          </dl>

          {/*
           * ⚠ TASLAKTA KIRILIM BOŞTUR ve bu doğru davranıştır: hangi kutunun
           * hangi matrahı taşıyacağı DÜZENLEME anındaki muameleye bağlıdır ve o
           * an dondurulur. Boş bir kutu listesi göstermek yerine sebebini
           * yazmak, "kutular kayboldu" sanısını engelliyor.
           */}
          <p className="mt-4 border-t border-[var(--c-border)] pt-3 text-xs text-[var(--c-ink-faint)]">
            KDV kırılımı (kutu eşlemesi ve zorunlu ibare) taslakta HENÜZ YOKTUR: belge
            düzenlendiğinde kurulur ve dondurulur.
          </p>

          {invoice.lines && invoice.lines.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-[var(--c-ink-soft)]">
              {invoice.lines.map((line) => (
                <li key={line.id} className="flex justify-between gap-2">
                  <span className="truncate">
                    {line.lineNo}. {line.description}
                  </span>
                  <span className="tabular whitespace-nowrap">
                    {amount(line.lineTotalExclVat)} · {percent(line.vatRate)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-[var(--c-ink-soft)]">{label}</dt>
      <dd className={strong ? 'tabular font-semibold' : 'tabular'}>{value}</dd>
    </div>
  );
}
